import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import SearchIcon from "@mui/icons-material/Search";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { HideIfNoRole } from "../../../auth/authUi";
import AxionControls from "../../../components/controls/AxionControls";
import { activateSparePart, deactivateSparePart, getSpareParts } from "../../../fetchers/cmmsSparePart";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { CMMS_MANAGER_ROLES } from "../../cmms/cmmsRoles";
import cmmsMessages from "../../cmms/cmmsMessages";
import { CmmsEmptyState, CmmsErrorState, formatCmmsError } from "../../cmms/components/CmmsStates";
import {
  cmmsTypeScale,
  getAssetHierarchyHeaderSx,
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getPageHeaderIconSx,
  getPageSubtitleSx,
  getPageTitleSx,
  getStatLabelSx,
  getStatValueSx,
  getStatisticTileSx,
  getSwitchSx,
} from "../../cmms/assetHierarchyStyles";
import messages from "../messages";
import { filterSpareParts } from "../sparePartSearch";
import SparePartsDataGrid from "./SparePartsDataGrid";
import SparePartFormDialog from "./SparePartFormDialog";
import SparePartStockDrawer from "./SparePartStockDrawer";

/*
 * The spare parts register: grid, filters, form dialog and stock drawer.
 *
 * Same split as the recurring schedules page: index.jsx is the shell, this does the work.
 */
export default function SparePartsPanel() {
  const theme = useTheme();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const [companyKey] = useLocalStorage("companyKey", 1);

  const [includeInactive, setIncludeInactive] = useState(false);
  const [belowReorderOnly, setBelowReorderOnly] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [formState, setFormState] = useState({ open: false, sparePart: null });
  const [stockPartId, setStockPartId] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  const sparePartsQuery = useQuery(
    ["cmmsSpareParts", companyKey, includeInactive],
    () => getSpareParts(companyKey, includeInactive),
    { enabled: Boolean(companyKey), staleTime: 30000 },
  );

  const spareParts = useMemo(() => sparePartsQuery.data ?? [], [sparePartsQuery.data]);

  // Client-side. The flag is already on every row — asking the API again for a boolean we are
  // holding would be a request that answers a question we can read. The same goes for the search
  // box: the register arrives whole, so the text is here to be matched.
  const visibleParts = useMemo(() => {
    const byReorder = belowReorderOnly
      ? spareParts.filter((part) => part.isBelowReorderPoint)
      : spareParts;
    return filterSpareParts(byReorder, searchText);
  }, [spareParts, belowReorderOnly, searchText]);

  const belowReorderCount = useMemo(
    () => spareParts.filter((part) => part.isBelowReorderPoint).length,
    [spareParts],
  );

  /*
   * The drawer follows the row, not a copy of it.
   *
   * Holding the id and looking the row up again on every render is what makes the balance in
   * the drawer move the moment a booking invalidates the register query. Keeping the row object
   * in state instead would leave the drawer showing the number the part had when it was opened,
   * next to a ledger that had just been updated.
   */
  const stockPart = useMemo(
    () => spareParts.find((part) => part.id === stockPartId) ?? null,
    [spareParts, stockPartId],
  );

  const retireMutation = useMutation({
    mutationFn: (id) => deactivateSparePart(companyKey, id),
  });

  const restoreMutation = useMutation({
    mutationFn: (id) => activateSparePart(companyKey, id),
  });

  const hasActiveFilters = includeInactive || belowReorderOnly || searchText.trim() !== "";

  const handleClearFilters = () => {
    setIncludeInactive(false);
    setBelowReorderOnly(false);
    setSearchText("");
  };

  const handleAdd = () => setFormState({ open: true, sparePart: null });
  const handleEdit = (row) => setFormState({ open: true, sparePart: row });
  const closeForm = () => setFormState({ open: false, sparePart: null });

  const handleRetire = (row) => {
    setConfirmation({
      title: intl.formatMessage(messages.retireTitle),
      message: intl.formatMessage(messages.retireMessage, { name: `${row.partNumber} · ${row.name}` }),
      confirmText: intl.formatMessage(messages.retireConfirm),
      onConfirm: async () => {
        try {
          await retireMutation.mutateAsync(row.id);
          setConfirmation(null);
          // The retired part leaves the default list, so a drawer standing open on it would
          // lose its row and blank out. Close it rather than leave a shell behind.
          if (stockPartId === row.id) setStockPartId(null);
          await queryClient.invalidateQueries({ queryKey: ["cmmsSpareParts"] });
          pushNotification(intl.formatMessage(messages.toastRetired));
        } catch (error) {
          pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.retireFailed)));
        }
      },
    });
  };

  /*
   * Restoring keeps the drawer open where retiring closes it.
   *
   * The two are not mirror images: a retired part leaves the default list, so a drawer standing
   * on it would lose its row. Coming back it gains one, and the drawer is where someone found
   * the stranded balance in the first place — closing it would take them away from the thing
   * they came to fix the moment it became fixable.
   */
  const handleRestore = (row) => {
    setConfirmation({
      title: intl.formatMessage(messages.restoreTitle),
      message: intl.formatMessage(messages.restoreMessage, { name: `${row.partNumber} · ${row.name}` }),
      confirmText: intl.formatMessage(messages.restoreConfirm),
      onConfirm: async () => {
        try {
          await restoreMutation.mutateAsync(row.id);
          setConfirmation(null);
          await queryClient.invalidateQueries({ queryKey: ["cmmsSpareParts"] });
          pushNotification(intl.formatMessage(messages.toastRestored));
        } catch (error) {
          pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.restoreFailed)));
        }
      },
    });
  };

  const isMutating = retireMutation.isPending || retireMutation.isLoading
    || restoreMutation.isPending || restoreMutation.isLoading;

  return (
    <Box>
      <Box sx={getAssetHierarchyHeaderSx(cmmsColors)}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Inventory2Icon sx={getPageHeaderIconSx(cmmsColors)} />
            <Box>
              <Typography variant={cmmsTypeScale.pageTitle} sx={getPageTitleSx(cmmsColors)}>
                <FormattedMessage {...messages.pageTitle} />
              </Typography>
              <Typography variant={cmmsTypeScale.pageSubtitle} sx={getPageSubtitleSx(cmmsColors)}>
                <FormattedMessage {...messages.pageSubtitle} />
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {/*
              * The reorder flag's whole reason for existing. A level nobody is shown is a level
              * nobody acts on, and the number belongs where someone opening the page will see it
              * without filtering for it first.
              */}
            <Box sx={getStatisticTileSx(cmmsColors)}>
              <Typography variant={cmmsTypeScale.statLabel} sx={getStatLabelSx(cmmsColors)}>
                <FormattedMessage {...messages.statBelowReorderPoint} />
              </Typography>
              <Typography
                variant={cmmsTypeScale.statValue}
                sx={{ ...getStatValueSx(cmmsColors), ...(belowReorderCount > 0 ? { color: "warning.main" } : {}) }}
              >
                {belowReorderCount} / {spareParts.length}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          sx={{ mt: 2, pt: 2, borderTop: `1px solid ${cmmsColors.border}`, gap: 1 }}
        >
          {/*
            * The module's own button styling, not MUI's default. palette.primary.main is
            * colors.primary[500], and so is the header surface behind this button — the same hex
            * — so a bare contained button is invisible until you hover it. getContainedButtonSx
            * puts the accent green on it, which is what the asset hierarchy's buttons do.
            */}
          <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={getContainedButtonSx(cmmsColors)}
            >
              <FormattedMessage {...messages.addSparePart} />
            </Button>
          </HideIfNoRole>

          {/*
            * Controlled, unlike the activities module's search box. Clear filters has
            * to be able to empty it, otherwise the button leaves the register looking filtered
            * with the reason still sitting in the field.
            */}
          {/*
            * Wide enough for its own label. MUI ellipsis-clips an outlined label that outgrows
            * the notch, and this one names four fields — at 280px it read "Search by part
            * number, na…", which hides exactly the part telling you location is searched.
            */}
          <TextField
            size="small"
            sx={{ width: { xs: "100%", sm: 420 } }}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            label={intl.formatMessage(messages.filterSearch)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={belowReorderOnly}
                onChange={(event) => setBelowReorderOnly(event.target.checked)}
                sx={getSwitchSx(cmmsColors)}
              />
            }
            label={intl.formatMessage(messages.filterBelowReorderPoint)}
          />

          {/* Server-side: the register endpoint owns which rows exist, not the grid. */}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeInactive}
                onChange={(event) => setIncludeInactive(event.target.checked)}
                sx={getSwitchSx(cmmsColors)}
              />
            }
            label={intl.formatMessage(messages.filterShowRetired)}
          />
        </Stack>
      </Box>

      <Box sx={{ mt: 2 }}>
        {sparePartsQuery.error &&
          <CmmsErrorState
            error={sparePartsQuery.error}
            fallback={intl.formatMessage(messages.loadFailed)}
            onRetry={() => sparePartsQuery.refetch()}
            isRetrying={sparePartsQuery.isFetching}
          />
        }
        {!sparePartsQuery.error &&
          <SparePartsDataGrid
            spareParts={visibleParts}
            // First load only, so a background refetch does not replace the list with a
            // skeleton — same note as cmmsWorkOrders/index.jsx.
            isLoading={sparePartsQuery.isLoading}
            showStatusColumn={includeInactive}
            onEdit={handleEdit}
            onOpenStock={(row) => setStockPartId(row.id)}
            onRetire={handleRetire}
            onRestore={handleRestore}
            emptyState={
              hasActiveFilters
                ? <CmmsEmptyState
                    icon={<FilterAltOffIcon />}
                    title={intl.formatMessage(messages.emptyFilteredTitle)}
                    hint={intl.formatMessage(messages.emptyFilteredHint)}
                    action={
                      <Button size="small" variant="outlined" color="secondary" onClick={handleClearFilters}>
                        <FormattedMessage {...messages.filterClear} />
                      </Button>
                    }
                  />
                : <CmmsEmptyState
                    icon={<Inventory2Icon />}
                    title={intl.formatMessage(messages.emptyTitle)}
                    hint={intl.formatMessage(messages.emptyHint)}
                    action={
                      <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
                        {/* Styled like the header's copy, so the same action is the same button
                            wherever the page offers it. */}
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleAdd}
                          sx={getContainedButtonSx(cmmsColors)}
                        >
                          <FormattedMessage {...messages.addSparePart} />
                        </Button>
                      </HideIfNoRole>
                    }
                  />
            }
          />
        }
      </Box>

      <SparePartFormDialog
        open={formState.open}
        sparePart={formState.sparePart}
        companyKey={companyKey}
        onClose={closeForm}
      />

      <SparePartStockDrawer
        sparePart={stockPart}
        companyKey={companyKey}
        onClose={() => setStockPartId(null)}
        onRestore={handleRestore}
      />

      <AxionControls.AxionConfirmation
        open={Boolean(confirmation)}
        title={confirmation?.title}
        message={confirmation?.message}
        confirmText={confirmation?.confirmText ?? intl.formatMessage(cmmsMessages.confirm)}
        cancelText={intl.formatMessage(cmmsMessages.cancel)}
        onConfirm={confirmation?.onConfirm}
        onCancel={() => setConfirmation(null)}
      />
      <AxionControls.AxionBackdrop open={isMutating} />
    </Box>
  );
}
